import { Box, Colors, IcUpload, Text } from 'basic-ui-lib';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

interface FileConfig {
  file?: File;
  thumb?: string;
}

export interface UploadFileProps {
  value?: FileConfig | null;
  onChange?: (val: FileConfig | null) => void;
  style?: React.CSSProperties;
  className?: string;
}

const UploadFile = ({ value, onChange, style, className }: UploadFileProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files || !files.length) return;
    const reader = new FileReader();
    reader.onload = () => {
      onChange?.({
        file: files?.[0],
        thumb: reader.result as string,
      });
    };
    reader.readAsDataURL(files?.[0]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const isImage = (file?: File) => file?.type.startsWith('image/');
  const isVideo = (file?: File) => file?.type.startsWith('video/');

  return (
    <Container
      isDragging={isDragging}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragging(false);
      }}
      onDrop={handleDrop}
      style={style}
      className={className}
    >
      <HiddenInput ref={inputRef} type='file' accept='image/*, video/*' onChange={handleSelect} />
      {!value?.thumb ? (
        <Placeholder>
          <IcUpload width={24} height={24} />
          <Text title_h8 medium color='neutral.text.5'>
            Drag or drop file here
          </Text>
        </Placeholder>
      ) : (
        <ImageContainer>
          {isImage(value?.file) && <img src={value?.thumb} className='image' />}
          {isVideo(value?.file) && <video src={value?.thumb} className='image' autoPlay muted loop playsInline />}

          <Overlay>
            <IcUpload width={24} height={24} />
            <Text title_h8 medium>
              Click or drag to replace
            </Text>
          </Overlay>
        </ImageContainer>
      )}
    </Container>
  );
};

export default UploadFile;

const Container = styled.div<{ isDragging: boolean }>`
  border: 2px dashed ${({ isDragging }) => (isDragging ? Colors.primary[5] : Colors.neutral.border[2])};
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 320px;
  border-radius: 8px;

  &:hover {
    border-color: ${Colors.primary[5]};
  }
`;

const HiddenInput = styled.input`
  display: none !important;
`;

const Placeholder = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background-color: #f1f4f8;
`;

const ImageContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 8px;

  position: relative;

  &:hover {
    & > div {
      display: flex;
    }
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const Overlay = styled.div`
  position: absolute;
  background-color: ${Colors.neutral.bg[3]};
  opacity: 0.8;
  display: none;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
  top: 0;
  border-radius: 8px;
`;
